import VisuallyHidden from "@reach/visually-hidden";
import { Component, Fragment, createRef } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { themeGet } from "styled-system";

import { StyledFontAwesomeIcon } from "../../partials/icons";
import Link from "../RouterFocusLink";
import { Label, Text } from "../text";

import { FrozenColumnTD, FrozenTableTD, StackingTD, TD } from "./TD";
import { FrozenTH, FrozenTableTH, TH } from "./TH";

import Tooltip, {
  HOVER_EVENTS,
  HOVER_EVENTS_OFF
} from "pstat-design-system/Tooltip";
import Checkbox, {
  ALL_SELECTED,
  CheckboxGroup,
  SOME_SELECTED,
  SelectAllCheckbox
} from "pstat-design-system/inputs/Checkbox";
import titleCase from "pstat-anywhere/utils/titleCase";
import { withLabelContext } from "pstat-anywhere/context_providers/LabelContext";

export const Cell = withLabelContext(
  ({
    color = "secondary.0",
    TDComp = TD,
    CellSpan = Text,
    to,
    TDRef,
    action,
    onActionClick,
    selectedCheckboxPks,
    disabledTooltipTextKey,
    pk,
    pl,
    labels,
    ...props
  }) => {
    const { t } = useTranslation();
    const disabledTooltipProps = {
      "data-tip": true,
      "data-for": `checkbox-disabled-tooltip-${pk}`,
      "data-event": HOVER_EVENTS,
      "data-event-off": HOVER_EVENTS_OFF
    };
    let checkboxProps = {
      value: `action-checkbox-${pk}`,
      testId: `action-checkbox-${pk}`,
      labelHeight: "16px",
      // want the html to have disabled be true or false and not the string value of the tooltip key
      disabled: disabledTooltipTextKey ? true : false
    };
    if (disabledTooltipTextKey) {
      checkboxProps = { ...checkboxProps, ...disabledTooltipProps };
    }
    return (
      <TDComp tabIndex="-1" {...props} py={0} pl={0} innerRef={TDRef}>
        {props.label && <VisuallyHidden>{`${props.label},`}</VisuallyHidden>}
        <CellSpan
          color={props.valid ? color : "messages.error"}
          fontStyle={props.fontStyle ? props.fontStyle : ""}
          fontSize={props.fontSize}
          to={to || null}
          py={3}
          pl={pl || 1}
          width="100%"
          height="100%"
          display="block"
        >
          {props.valid ? (
            ""
          ) : (
            <StyledFontAwesomeIcon icon={["fal", "exclamation-triangle"]} />
          )}{" "}
          {action && action === "edit" && (
            <Fragment>
              <StyledFontAwesomeIcon
                data-tip
                data-for="edit-icon-tooltip"
                data-event={HOVER_EVENTS}
                data-event-off={HOVER_EVENTS_OFF}
                data-delay-hide="2000"
                icon={["fas", "edit"]}
                size="xl"
              />
              <Tooltip name="edit-icon-tooltip">
                {t("documentControl.view.secondaryNav.navOptions.edit.name")}
              </Tooltip>
            </Fragment>
          )}
          {action && action === "check" && (
            <CheckboxGroup
              py={0}
              px={0}
              name={`action-${pk}`}
              onChange={() => {
                if (!disabledTooltipTextKey) {
                  onActionClick(pk);
                }
              }}
              selectedValues={
                selectedCheckboxPks &&
                selectedCheckboxPks.map(pk => `action-checkbox-${pk}`)
              }
            >
              <Checkbox {...checkboxProps} />
              {disabledTooltipTextKey && (
                <Tooltip name={`checkbox-disabled-tooltip-${pk}`}>
                  {t(disabledTooltipTextKey, {
                    documentLabelPlural: labels.documentLabelPlural.toLowerCase(),
                    documentLabelPluralTitle: titleCase(
                      labels.documentLabelPlural
                    )
                  })}
                </Tooltip>
              )}
            </CheckboxGroup>
          )}
          {props.children}
        </CellSpan>
      </TDComp>
    );
  }
);

Cell.defaultProps = {
  valid: 1
};

export const StackingCell = ({ to, ...props }) => {
  const { rowKey, rowIndex, columnIndex, ...otherProps } = props;
  return (
    <Cell
      TDComp={StackingTD}
      CellSpan={to ? Link : Text}
      {...otherProps}
      to={to}
    />
  );
};

export class FrozenTableCell extends Component {
  constructor(props) {
    super(props);

    this.frozenTD = createRef();
  }

  componentDidMount() {
    const td = this.frozenTD.current;
    if (td) {
      const tr = td.parentElement;
      if (td.clientHeight > tr.clientHeight) {
        tr.style.height = `${td.clientHeight}px`;
      }
    }
  }

  render() {
    if (this.props.column && this.props.column.frozen) {
      return (
        <Cell
          TDComp={FrozenColumnTD}
          CellSpan={Link}
          {...this.props}
          TDRef={this.frozenTD}
        />
      );
    }
    return (
      <Cell
        TDComp={FrozenTableTD}
        CellSpan={Link}
        {...this.props}
        minWidth={[200, 75, 50]}
        maxWidth={300}
      />
    );
  }
}

const HeadingLabel = styled(Label)`
  &.sort {
    color: ${themeGet("colors.secondary.0")};
    cursor: pointer;
  }
  &.sort-asc,
  &.sort-desc {
    text-decoration: underline;
  }
`;

export const HeaderCell = ({ children, THComponent = TH, ...props }) => {
  let sortArrow = null;
  if (props.className) {
    if (props.className.indexOf("sort-asc") > -1) {
      sortArrow = (
        <StyledFontAwesomeIcon
          icon={["far", "sort-down"]}
          size="lg"
          color="secondary.0"
        />
      );
    } else if (props.className.indexOf("sort-desc") > -1) {
      sortArrow = (
        <StyledFontAwesomeIcon
          icon={["far", "sort-up"]}
          size="lg"
          color="secondary.0"
        />
      );
    }
  }
  return (
    <THComponent style={props.style}>
      {props.action && props.action === "selectAllCheckbox" ? (
        <CheckboxGroup
          py={0}
          px={0}
          name="select-all"
          onChange={() => props.onSelectAllCheckboxClick()}
          selectedValues={
            props.selectAllCheckboxState === ALL_SELECTED ||
            props.selectAllCheckboxState === SOME_SELECTED
              ? ["select-all-checkbox"]
              : []
          }
        >
          <SelectAllCheckbox
            id="select-all-checkbox"
            ariaLabel="select all"
            value="select-all-checkbox"
            testId="select-all-checkbox"
            labelHeight={"16px"}
            someSelected={props.selectAllCheckboxState === SOME_SELECTED}
          />
        </CheckboxGroup>
      ) : (
        <Fragment>
          <HeadingLabel {...props}>{children}</HeadingLabel>
          {sortArrow}
        </Fragment>
      )}
    </THComponent>
  );
};

export const FrozenTableHeaderCell = props => {
  let THComponent = FrozenTableTH;
  if (props.frozen) {
    THComponent = FrozenTH;
  }
  return <HeaderCell THComponent={THComponent} {...props} />;
};
